import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../../../context/AuthContext";
import { controleProducaoService } from "../../../../services/controleProducaoService";
import type {
  AssinaturaTvSetor,
  LojaProgressoTv,
  ProgressoItensTv,
} from "../../../../services/controleProducaoService";
import { lojaService } from "../../../../services/lojaService";
import { Loja } from "../../../../types/loja";
import { useRankingEvents } from "../../../../hooks/useRankingEvents";
import { RANKING_SSE_QUERY_KEYS } from "../../../../constants/rankingEventQueryKeys";

const GERAL_VAZIO: ProgressoItensTv = {
  itensTotal: 0,
  itensResolvidos: 0,
  pct: 0,
};

interface RankingFichaSetoresContextData {
  lojasTv: LojaProgressoTv[];
  geral: ProgressoItensTv;
  conferencia: AssinaturaTvSetor | null;
  producaoTotal: AssinaturaTvSetor | null;
  lojas: Loja[];
  isLoading: boolean;
  selectedLojaId?: number;
  showLojaSelector: boolean;
  dateLabel: string;
  selectedLojaNome?: string;
  toggleLojaSelector: () => void;
  selectAllLojas: () => void;
  selectLoja: (id: number) => void;
}

const RankingFichaSetoresContext = createContext<RankingFichaSetoresContextData>(
  {} as RankingFichaSetoresContextData
);

function formatDataFicha(iso: string | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

export const RankingFichaSetoresProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user } = useAuth();
  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    () => user?.lojaId
  );
  const [showLojaSelector, setShowLojaSelector] = useState(false);

  const { data: lojas = [], isLoading: isLoadingLojas } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const rankingFilters = useMemo(
    () => ({ lojaId: selectedLojaId ?? null }),
    [selectedLojaId]
  );

  const { data, isLoading: isLoadingRanking } = useQuery({
    queryKey: ["ranking-ficha-setores", rankingFilters],
    queryFn: () =>
      controleProducaoService.getRankingSetores({
        lojaId: rankingFilters.lojaId ?? undefined,
      }),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });

  useRankingEvents("ficha-diaria", selectedLojaId, {
    queryKeys: RANKING_SSE_QUERY_KEYS.rankingFichaSetores,
  });

  const isLoading = isLoadingLojas || isLoadingRanking;

  const dateLabel = formatDataFicha(data?.dataFicha);
  const selectedLojaNome = useMemo(
    () =>
      data?.lojaNome || lojas.find((l) => l.id === selectedLojaId)?.nome,
    [lojas, selectedLojaId, data?.lojaNome]
  );

  const toggleLojaSelector = useCallback(() => {
    setShowLojaSelector((prev) => !prev);
  }, []);

  const selectAllLojas = useCallback(() => {
    setSelectedLojaId(undefined);
    setShowLojaSelector(false);
  }, []);

  const selectLoja = useCallback((id: number) => {
    setSelectedLojaId(id);
    setShowLojaSelector(false);
  }, []);

  return (
    <RankingFichaSetoresContext.Provider
      value={{
        lojasTv: data?.lojas ?? [],
        geral: data?.geral ?? GERAL_VAZIO,
        conferencia: data?.conferencia ?? null,
        producaoTotal: data?.producaoTotal ?? null,
        lojas,
        isLoading,
        selectedLojaId,
        showLojaSelector,
        dateLabel,
        selectedLojaNome,
        toggleLojaSelector,
        selectAllLojas,
        selectLoja,
      }}
    >
      {children}
    </RankingFichaSetoresContext.Provider>
  );
};

export const useRankingFichaSetores = (): RankingFichaSetoresContextData => {
  const context = useContext(RankingFichaSetoresContext);
  if (!context) {
    throw new Error(
      "useRankingFichaSetores must be used within RankingFichaSetoresProvider"
    );
  }
  return context;
};
